import React from "react";
import { Building2, CreditCard, Plus, Users } from "lucide-react";
import { Button } from "@/components/ui/button";

const OwnerDashboard = ({
  user,
  pendingSales,
  representationRequests,
  setShowConfirmSaleModal,
  setSelectedSaleNotification,
  setSaleConfirmation,
  rejectSale,
  handleApproveRepresentation,
  handleRejectRepresentation,
  propertyCount,
  activePropertyCount,
  navigate,
}) => {
  const isOwnerLike = user?.role === "owner" || user?.role === "admin";
  if (!isOwnerLike) return null;

  const pendingRepresentationCount =
    representationRequests?.filter((r) => r.status === "pending").length || 0;

  return (
    <>
      <div className="bg-background border border-border rounded-2xl p-5 md:p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-5">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">
              Propietario Pro
            </p>
            <h2
              className="text-2xl md:text-3xl font-bold"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              Resumen de operación Pro
            </h2>
            <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
              Un flujo directo para publicar, medir el avance de tu plan y responder con rapidez a nuevas oportunidades.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => navigate("/add-property")}>
              <Plus className="w-4 h-4 mr-2" /> Nueva Propiedad
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate("/account?tab=subscription")}
            >
              <CreditCard className="w-4 h-4 mr-2" /> Mi Cuenta
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs text-muted-foreground mb-1">Publicación</p>
            <p className="text-lg font-semibold">{activePropertyCount} activas</p>
            <p className="text-sm text-muted-foreground mt-2">
              {propertyCount} de 10 propiedades del plan Pro.
            </p>
            <div className="mt-3">
              <div className="flex justify-between text-xs text-muted-foreground mb-1.5">
                <span>{propertyCount} de 10</span>
                <span>{Math.round((propertyCount / 10) * 100)}%</span>
              </div>
              <div className="w-full h-2 bg-border rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    propertyCount >= 9 ? "bg-red-500" : propertyCount >= 7 ? "bg-yellow-500" : "bg-green-500"
                  }`}
                  style={{ width: `${Math.min((propertyCount / 10) * 100, 100)}%` }}
                />
              </div>
              {propertyCount >= 9 && (
                <p className="text-xs text-red-600 font-semibold mt-2">
                  Estás cerca del límite. Cambia de rol si necesitas operar con inventario ilimitado.
                </p>
              )}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs text-muted-foreground mb-1">Solicitudes</p>
            <p className="text-lg font-semibold">{pendingRepresentationCount} pendientes</p>
            <p className="text-sm text-muted-foreground mt-2">
              Revisa brokers interesados y decide a quien aprobar para representar.
            </p>
          </div>
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <p className="text-xs text-muted-foreground mb-1">Cierres</p>
            <p className="text-lg font-semibold">{pendingSales.length} por confirmar</p>
            <p className="text-sm text-muted-foreground mt-2">
              Confirma ventas cuando un lead calificado ya está listo para cerrar.
            </p>
          </div>
        </div>
      </div>

      {/* Leads Calificados Pendientes de Confirmar */}
      {pendingSales.length > 0 && (
        <div
          className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-lg p-4 sm:p-6 mb-6"
          data-testid="pending-sales"
        >
          <div className="flex items-center gap-3 mb-4">
            <h2
              className="text-lg sm:text-2xl font-bold flex items-center gap-3"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
            >
              <Users className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
              Leads Calificados ({pendingSales.length})
            </h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            Brokers te notifican cuando un lead está listo para cerrar. Confirma
            la venta para actualizar el sistema.
          </p>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {pendingSales.map((sale) => (
              <div
                key={sale.id}
                className="bg-white border border-amber-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <h3 className="font-semibold">{sale.item_title}</h3>
                    <p className="text-sm text-muted-foreground">
                      Cliente:{" "}
                      <span className="font-medium">{sale.client_name}</span>
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Broker:{" "}
                      <span className="font-medium">{sale.broker_name}</span>
                    </p>
                    {sale.notes && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Nota: {sale.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedSaleNotification(sale);
                        setSaleConfirmation({ sale_price: "", notes: "" });
                        setShowConfirmSaleModal(true);
                      }}
                    >
                      Confirmar Venta
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        const reason = window.prompt(
                          "Razón del rechazo (opcional):",
                        );
                        rejectSale(sale.id, reason || "");
                      }}
                    >
                      Rechazar
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Solicitudes de Brokers */}
      {pendingRepresentationCount > 0 && (
        <div
          className="bg-background border-2 border-primary/30 rounded-lg p-4 sm:p-6 mb-6"
          data-testid="broker-requests"
        >
          <h2 className="text-lg sm:text-xl font-bold mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Solicitudes de Representación ({pendingRepresentationCount})
          </h2>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {representationRequests
              ?.filter((r) => r.status === "pending")
              .map((req) => (
                <div
                  key={req.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-muted/30 rounded-lg border border-border"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{req.broker_name}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {req.item_type === "property"
                        ? "Propiedad"
                        : "Desarrollo"}
                      : {req.item_title} | Comisión: {req.commission_percentage}
                      %
                    </p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm"
                      onClick={() =>
                        handleApproveRepresentation(req.property_id, req.id)
                      }
                    >
                      Aprobar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        handleRejectRepresentation(req.property_id, req.id)
                      }
                    >
                      Rechazar
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </>
  );
};

export { OwnerDashboard };
